/**
 * Makers-specific utility functions
 */

import type { EmployeeAssignment, EmployeeSummary } from "@/hooks/useMakersBoardData";
import type { Assignment } from "@/hooks/useProjectBoardData";
import { addDays, parseISODate } from "./dateUtils";
import { getDefaultWeekCount, getStartOfWeek, isoKey, type WeekColumn } from "./weekUtils";

const MILLISECONDS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

/**
 * Availability computation mode. Drives the 3-way toggle on the Makers and
 * Availability boards that replaced the old "Include unconfirmed" checkbox:
 *
 *  - "all":       count every assignment, confirmed or tentative (unlocked lock).
 *  - "confirmed": count only confirmed assignments (locked lock).
 *  - "sold":      true availability against sold work — count only confirmed
 *                 assignments on Won ("sold") projects. Inherently excludes
 *                 unconfirmed assignments, even within a sold project.
 */
export type AvailabilityMode = "all" | "confirmed" | "sold";

/** All availability modes, in toggle order. */
export const AVAILABILITY_MODES: readonly AvailabilityMode[] = ["all", "confirmed", "sold"] as const;

/** Narrow an unknown (e.g. a persisted setting) to a valid AvailabilityMode. */
export function isAvailabilityMode(value: unknown): value is AvailabilityMode {
  return value === "all" || value === "confirmed" || value === "sold";
}

/** The reserved "won" deal-stage kind that marks a project as sold. */
const SOLD_DEAL_STAGE_KIND = "won";

/**
 * Whether an assignment's hours count toward an employee's allocation under the
 * given availability mode (see AvailabilityMode for the per-mode rules).
 *
 * Both "confirmed" and "sold" exclude tentative (unconfirmed) assignments;
 * "sold" additionally requires the linked project to be in the Won stage.
 */
export function assignmentCountsForMode(
  assignment: {
    is_confirmed?: boolean | null;
    project?: { deal_stage?: { kind?: string | null } | null } | null;
  },
  mode: AvailabilityMode,
): boolean {
  if (mode === "all") return true;
  if (!assignment.is_confirmed) return false;
  if (mode === "sold") return assignment.project?.deal_stage?.kind === SOLD_DEAL_STAGE_KIND;
  return true;
}

/**
 * Exclude assignments whose linked project is in the reserved "lost" deal stage.
 *
 * The deal stage is embedded from the deal_stages table (joined by id); the
 * semantic kind lives at `project.deal_stage.kind` ('open' | 'won' | 'lost').
 *
 * Used to prevent lost-stage projects from contributing to maker availability
 * math or appearing in a maker's project list.
 */
export function excludeLostProjectAssignments<
  T extends { project: { deal_stage?: { kind?: string | null } | null } | null }
>(assignments: T[]): T[] {
  return assignments.filter((a) => a.project?.deal_stage?.kind !== "lost");
}

/**
 * Check if a project is currently active (its date range overlaps the current week)
 */
export function isProjectActive(startDate: string, numWeeks: number): boolean {
  const thisWeekMonday = getStartOfWeek(new Date());
  const projectStart = getStartOfWeek(parseISODate(startDate));
  const projectEnd = addDays(projectStart, numWeeks * 7 - 1);
  return projectStart <= thisWeekMonday && projectEnd >= thisWeekMonday;
}

/**
 * Week allocation for an employee
 */
export interface WeekAllocation {
  totalHours: number;
  hasAllocation: boolean;
}

/**
 * Week paired with its allocation data
 */
export interface WeekAllocationEntry {
  week: WeekColumn;
  allocation: WeekAllocation;
}

export function getProjectWeekIndex(
  projectStartDate: string,
  week: WeekColumn
): number | null {
  const projectStart = parseISODate(projectStartDate);
  const projectStartWeek = getStartOfWeek(projectStart);
  const weekStart = getStartOfWeek(week.start);

  const diffMs = weekStart.getTime() - projectStartWeek.getTime();
  const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));

  return diffWeeks >= 0 ? diffWeeks : null;
}

export function getHoursForWeek(
  assignment: EmployeeAssignment | Assignment,
  projectStartDate: string,
  projectNumWeeks: number,
  week: WeekColumn
): number {
  const weekIndex = getProjectWeekIndex(projectStartDate, week);
  if (weekIndex === null || weekIndex >= projectNumWeeks) return 0;

  const hoursArray = assignment.weekly_hours_allocated;
  if (!Array.isArray(hoursArray) || weekIndex >= hoursArray.length) return 0;

  return hoursArray[weekIndex] ?? 0;
}

function hasCurrentOrFutureActiveProjectHours(
  assignment: EmployeeAssignment,
  asOfDate: Date
): boolean {
  const project = assignment.project;
  if (!project?.start_date || !project.num_weeks) return false;
  if (project.deal_stage?.kind === "lost") return false;

  const hoursArray = assignment.weekly_hours_allocated;
  if (!Array.isArray(hoursArray)) return false;

  const projectStart = getStartOfWeek(parseISODate(project.start_date));
  const currentWeekStart = getStartOfWeek(asOfDate);
  const projectEnd = addDays(projectStart, project.num_weeks * 7 - 1);
  if (projectEnd < currentWeekStart) return false;

  const firstRelevantWeek = Math.max(
    0,
    Math.floor(
      (currentWeekStart.getTime() - projectStart.getTime()) /
        MILLISECONDS_PER_WEEK
    )
  );
  const lastRelevantWeek = Math.min(project.num_weeks, hoursArray.length);

  for (let index = firstRelevantWeek; index < lastRelevantWeek; index += 1) {
    if ((hoursArray[index] ?? 0) > 0) return true;
  }
  return false;
}

export function hasCurrentOrFutureActiveProjectAllocation(
  assignments: EmployeeAssignment[],
  asOfDate: Date = new Date()
): boolean {
  return assignments.some((assignment) =>
    hasCurrentOrFutureActiveProjectHours(assignment, asOfDate)
  );
}

export function shouldIncludeEmployeeInMakersList(
  employee: EmployeeSummary,
  asOfDate: Date = new Date()
): boolean {
  if (employee.is_active !== false) return true;
  return hasCurrentOrFutureActiveProjectAllocation(employee.assignments, asOfDate);
}

/**
 * Build week allocations for assignments filtered by project
 */
export function buildWeekAllocations(
  assignments: EmployeeAssignment[] | Assignment[],
  weeks: WeekColumn[],
  projectStartDate?: string,
  projectNumWeeks?: number,
): WeekAllocationEntry[] {
  if (!projectStartDate || !projectNumWeeks) {
    return weeks.map((week) => ({
      week,
      allocation: { totalHours: 0, hasAllocation: false },
    }));
  }

  return weeks.map((week) => {
    let totalHours = 0;
    let hasAllocation = false;

    assignments.forEach((assignment) => {
      const hours = getHoursForWeek(assignment, projectStartDate, projectNumWeeks, week);
      if (hours > 0) {
        totalHours += hours;
        hasAllocation = true;
      }
    });

    return {
      week,
      allocation: { totalHours, hasAllocation },
    };
  });
}

/**
 * Project allocation data
 */
export interface ProjectAllocation {
  project: {
    id: string;
    name: string | null;
    client: string | null;
    color?: string | null;
  } | null;
  projectId: string | null;
  assignments: EmployeeAssignment[];
  weekAllocations: WeekAllocationEntry[];
}


/**
 * Unified employee row model with all computed data
 */
export interface EmployeeRowModel {
  employee: EmployeeSummary;
  projects: ProjectAllocation[];
  hasMultipleProjects: boolean;
  hasAssignments: boolean;
  isOverAllocated: boolean;
}

function hasHoursInWeeks(
  assignment: EmployeeAssignment | Assignment,
  projectStartDate: string | undefined,
  projectNumWeeks: number | undefined,
  weeks: WeekColumn[]
): boolean {
  if (!projectStartDate || !projectNumWeeks) return false;
  return weeks.some((week) => {
    const hours = getHoursForWeek(assignment, projectStartDate, projectNumWeeks, week);
    return hours > 0;
  });
}

function assignmentsOverlapWeeks(
  assignments: EmployeeAssignment[],
  weeks: WeekColumn[],
  projectStartDate?: string,
  projectNumWeeks?: number
): boolean {
  return assignments.some((assignment) =>
    hasHoursInWeeks(assignment, projectStartDate, projectNumWeeks, weeks)
  );
}

/**
 * Build a unified model for an employee row
 */
export function buildEmployeeRowModel(
  employee: EmployeeSummary,
  weeks: WeekColumn[],
  mode: AvailabilityMode = "all"
): EmployeeRowModel {
  // Group assignments by project
  const assignmentsByProject = new Map<string | null, EmployeeAssignment[]>();
  employee.assignments.forEach((assignment) => {
    const projectId = assignment.project?.id ?? null;
    const projectStartDate = (assignment.project as any)?.start_date;
    const projectNumWeeks = (assignment.project as any)?.num_weeks;

    // Drop assignments that don't count under the current availability mode
    // (tentative work, or non-sold work when showing sold availability).
    if (!assignmentCountsForMode(assignment, mode)) {
      return;
    }

    // Only add assignment if it has hours in any of the provided weeks
    if (assignmentsOverlapWeeks([assignment], weeks, projectStartDate, projectNumWeeks)) {
      if (!assignmentsByProject.has(projectId)) {
        assignmentsByProject.set(projectId, []);
      }
      assignmentsByProject.get(projectId)!.push(assignment);
    }
  });

  // Build project allocations
  const projects: ProjectAllocation[] = Array.from(assignmentsByProject.entries())
    .map(([projectId, assignments]) => {
      const project = assignments[0]?.project ?? null;
      const projectStartDate = (project as any)?.start_date;
      const projectNumWeeks = (project as any)?.num_weeks;
      return {
        project,
        projectId,
        assignments,
        weekAllocations: buildWeekAllocations(assignments, weeks, projectStartDate, projectNumWeeks),
      };
    });

  const capacity = employee.weekly_capacity_hours;
  const weekTotals = new Map<string, number>();

  projects.forEach((projectAlloc) => {
    projectAlloc.weekAllocations.forEach((entry) => {
      if (entry.allocation.hasAllocation) {
        const key = isoKey(entry.week);
        const current = weekTotals.get(key) ?? 0;
        weekTotals.set(key, current + entry.allocation.totalHours);
      }
    });
  });

  const isOverAllocated = Array.from(weekTotals.values()).some((total) => total > capacity);

  return {
    employee,
    projects,
    hasMultipleProjects: projects.length > 1,
    hasAssignments: employee.assignments.length > 0,
    isOverAllocated,
  };
}

export function assignmentOverlapsDateRange(
  assignment: EmployeeAssignment | Assignment,
  projectStartDate: string,
  projectNumWeeks: number,
  weekStart: Date,
  weekEnd: Date
): boolean {
  const projectStart = parseISODate(projectStartDate);
  const projectStartWeek = getStartOfWeek(projectStart);
  const projectEnd = addDays(projectStartWeek, projectNumWeeks * 7 - 1);

  return projectStart <= weekEnd && projectEnd >= weekStart;
}

/**
 * Check if an employee has the required hours available for a date range
 * Returns true if the employee has at least the required hours available in every week from startDate to endDate
 * If endDate is null, checks from startDate to a reasonable future date (DEFAULT_WEEK_COUNT weeks)
 */
export function hasAvailabilityForDateRange(
  employee: EmployeeSummary,
  startDate: Date,
  endDate: Date | null,
  requiredHours: number,
  mode: AvailabilityMode = "all",
): boolean {
  if (requiredHours <= 0) {
    return true;
  }

  const weekStart = getStartOfWeek(startDate);
  const effectiveEndDate = endDate ?? addDays(weekStart, (getDefaultWeekCount() - 1) * 7);
  const weekEndStart = getStartOfWeek(effectiveEndDate);

  const weeks: WeekColumn[] = [];
  let cursor = new Date(weekStart);
  while (cursor <= weekEndStart) {
    weeks.push({
      start: new Date(cursor),
      end: addDays(cursor, 6),
      label: cursor.toISOString(),
    });
    cursor = addDays(cursor, 7);
  }

  const capacity = employee.weeklyCapacityHours;

  for (const week of weeks) {
    let totalAllocatedHours = 0;

    employee.assignments.forEach((assignment) => {
      const assignmentProjectStart = (assignment.project as any)?.start_date;
      const assignmentProjectNumWeeks = (assignment.project as any)?.num_weeks;
      if (!assignmentProjectStart || !assignmentProjectNumWeeks) return;

      // Only count assignments that the current availability mode counts, so the
      // candidate filter matches the availability the board displays.
      if (!assignmentCountsForMode(assignment, mode)) return;

      if (assignmentOverlapsDateRange(assignment, assignmentProjectStart, assignmentProjectNumWeeks, week.start, week.end)) {
        const hours = getHoursForWeek(assignment, assignmentProjectStart, assignmentProjectNumWeeks, week);
        totalAllocatedHours += hours;
      }
    });

    const availableHours = capacity - totalAllocatedHours;
    if (availableHours < requiredHours) {
      return false;
    }
  }

  return true;
}
